import type React from 'react';
import {useState} from 'react';
import {Box, Button, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography} from '@mui/material';
import {Tag} from '~/components/tag/Tag';
import type {MarketActions} from '../../hooks/useMarket';
import type {MarketIndex} from '../../types/marketTypes';
import {NumberCell} from '../strategy/tools/NumberCell';
import {RowAction} from '../components/RowAction';
import {LOCALE} from '~/pages/category-profile/domain/locale';

const PROVIDER = {eurostat: 'Eurostat', ecb: 'ECB', databento: 'Databento', manual: 'Uploaded'} as Record<string, string>;

/** The category's cost drivers: confirmed ones with their weight, suggestions to confirm, and an add control. */
export const DriversSection: React.FC<{data: MarketIndex; actions: MarketActions}> = ({data, actions}) => {
    const [adding, setAdding] = useState('');
    const used = new Set(data.drivers.map(d => d.series.id));
    const addable = data.catalogue.filter(s => !used.has(s.id) && s.freshness !== 'empty');
    const fmt = (v: number | null, digits = 2) => (v === null ? '—' : v.toLocaleString(LOCALE, {maximumFractionDigits: digits}));

    return (
        <Stack gap={1.5}>
            <Box sx={{overflowX: 'auto'}}>
                <Table size="small" aria-label="Cost drivers">
                    <TableHead>
                        <TableRow>
                            <TableCell>Driver</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Weight %</TableCell>
                            <TableCell align="right">Latest</TableCell>
                            <TableCell align="right">YoY</TableCell>
                            <TableCell align="right">r vs price</TableCell>
                            <TableCell align="right">Sensitivity</TableCell>
                            <TableCell/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.drivers.length === 0 && (
                            <TableRow><TableCell colSpan={8}>
                                <Typography variant="body2" color="text.secondary">No drivers yet. Add one below.</Typography>
                            </TableCell></TableRow>
                        )}
                        {data.drivers.map(d => (
                            <TableRow key={d.series.id}>
                                <TableCell>
                                    <Typography variant="body2">{d.series.name}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {`${PROVIDER[d.series.provider] ?? d.series.provider} · ${d.series.unit}`}
                                        {d.series.freshness === 'stale' && ' · stale'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Tag size="small" solid={d.status === 'confirmed'} color={d.status === 'confirmed' ? 'success' : 'default'}
                                         label={d.status === 'confirmed' ? 'Confirmed' : 'Suggested'}/>
                                </TableCell>
                                <TableCell align="right">
                                    {d.status === 'confirmed' ? (
                                        <NumberCell value={Math.round(d.weight * 100)} min={0} max={100} disabled={!data.canEdit}
                                                    label={`Weight of ${d.series.name}`} onCommit={v => void actions.setWeight(d, v / 100)}/>
                                    ) : `${Math.round(d.weight * 100)}`}
                                </TableCell>
                                <TableCell align="right">{fmt(d.latestValue)}</TableCell>
                                <TableCell align="right">{d.yoyPct === null ? '—' : `${d.yoyPct > 0 ? '+' : ''}${d.yoyPct.toFixed(1)}%`}</TableCell>
                                <TableCell align="right">
                                    {d.r === null ? (
                                        <Tooltip title="Needs 6 months of price paid and driver data"><span>—</span></Tooltip>
                                    ) : (
                                        <Tooltip title={d.weak ? `Weak link: below ${data.correlationWarning}` : `Best at a ${d.lagMonths}-month lag`}>
                                            <Typography variant="body2" component="span" color={d.weak ? 'warning.dark' : 'text.primary'}>
                                                {`${d.r.toFixed(2)}${d.lagMonths ? ` (${d.lagMonths}m lag)` : ''}`}
                                            </Typography>
                                        </Tooltip>
                                    )}
                                </TableCell>
                                <TableCell align="right">{fmt(d.sensitivity)}</TableCell>
                                <TableCell align="right">
                                    {data.canEdit && (d.status === 'suggested'
                                        ? <Button size="small" onClick={() => void actions.confirm(d)}>Confirm</Button>
                                        : <RowAction kind="delete" label={d.series.name} onClick={() => void actions.remove(d)}/>)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
            {data.canEdit && addable.length > 0 && (
                <Stack direction="row" gap={1} alignItems="center">
                    <TextField select size="small" label="Add a driver" value={adding} sx={{minWidth: 320}}
                               onChange={e => setAdding(e.target.value)}>
                        {addable.map(s => <MenuItem key={s.id} value={String(s.id)}>{`${s.name} · ${PROVIDER[s.provider] ?? s.provider}`}</MenuItem>)}
                    </TextField>
                    <Button disabled={!adding} onClick={() => {
                        const s = addable.find(x => String(x.id) === adding);
                        if (s) void actions.add(s.id, s.name).then(() => setAdding(''));
                    }}>Add</Button>
                </Stack>
            )}
            <Typography variant="caption" color="text.secondary">
                Suggested drivers come from the category name and stay out of the driver mix until confirmed. r is the
                correlation of price paid with the driver (best of 0-3 months lag); sensitivity 0.3 means a 10% driver
                move went with a 3% price move.
            </Typography>
        </Stack>
    );
};
